import type { Layout } from "@/layout/layout-utils";
import { useState, useEffect, useCallback } from "react";
import { primaryLayout } from "./state-default";

const version = "v5";

// Panel visibility settings
export type PanelVisibility = {
  orderBookVisible: boolean;
  accountVisible: boolean;
};

const defaultPanelVisibility: PanelVisibility = {
  orderBookVisible: true,
  accountVisible: true,
};

export const usePanelVisibility = () => {
  const key = `${version}-panel-visibility`;
  return useLocalStorage<PanelVisibility>(key, defaultPanelVisibility);
};

export const usePanelState = <T>(panelId: string, initialState: T) => {
  const key = `${version}-layout-panel-state-${panelId}`;
  return useLocalStorage<T>(key, initialState);
};

export const useLayouts = () => {
  const key = `${version}-layouts`;
  return useLocalStorage<Layout[]>(key, [primaryLayout]);
};

export const useSelectedLayoutId = () => {
  const [layouts] = useLayouts();
  const key = `${version}-selected-layout-id`;
  return useLocalStorage(key, layouts[0]?.data.id ?? "");
};

export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const readValue = useCallback(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [key, initialValue]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);

        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  const onStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        setStoredValue(JSON.parse(e.newValue));
      }
    },
    [key]
  );

  useEffect(() => {
    window.addEventListener("storage", onStorageChange);

    return () => {
      window.removeEventListener("storage", onStorageChange);
    };
  }, [onStorageChange]);

  return [storedValue, setValue] as const;
};
